'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import HeaderNav from '@/components/HeaderNav';
import FooterNew from '@/components/FooterNew';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactModal from '@/components/ContactModal';
import {
  IconArrowRight,
  IconStepUnderstand,
  IconStepAware,
  IconStepTransform,
  IconStepComplete,
  IconStepCreate,
  IconStepLive,
  IconJourneyUnderstand,
  IconJourneyExperience,
  IconJourneyApply,
  IconJourneyTransform,
  IconJourneyLive,
  IconPhoneContact,
  IconWhatsAppContact,
  IconEmailContact,
} from '@/components/DesignIcons';
import { PHONE, PHONE_DISPLAY, EMAIL, WHATSAPP_LINK } from '@/config/constants';

export default function Home() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="page-wrapper">
      {/* Contact & Consultation Interactive Modal */}
      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      {/* 1. Header / Navigation */}
      <HeaderNav onOpenContact={() => setModalOpen(true)} />

      <main>
        {/* =========================================================================
            2. HERO SECTION
            ========================================================================= */}
        <section className="hero-section" id="hero">
          {/* Background image & gradient overlay */}
          <div className="hero-background-wrapper">
            <Image
              src="/images/new_design/hero_bg.jpg"
              alt="InnerTruth Mountain Sunrise"
              fill
              priority
              className="hero-bg-img"
              sizes="100vw"
            />
            <div className="hero-gradient-overlay" />
          </div>

          <div className="hero-content-container">
            {/* Left Column Content */}
            <div className="hero-text-col">
              <div className="hero-eyebrow-wrap">
                <span className="hero-eyebrow-main">INNERTRUTH</span>
                <span className="hero-eyebrow-divider"></span>
                <span className="hero-eyebrow-sub">ENRICHING LIVES</span>
              </div>

              <h1 className="hero-title">
                From Illusion<br />to Reality
              </h1>

              <h2 className="hero-subtitle">
                The 6 Steps to Human Excellence
              </h2>

              <p className="hero-steps-summary">
                Understand &nbsp;•&nbsp; Become Aware &nbsp;•&nbsp; Transform &nbsp;•&nbsp; Complete &nbsp;•&nbsp; Create &nbsp;•&nbsp; Live
              </p>

              <div className="hero-actions">
                <button
                  type="button"
                  className="btn-primary-pill"
                  onClick={() => setModalOpen(true)}
                >
                  Explore the Journey
                  <IconArrowRight className="btn-arrow" />
                </button>

                <a href="#programs" className="btn-secondary-pill">
                  Our Programs
                </a>
              </div>
            </div>

            {/* Right Sky Angled Handwritten Script */}
            <div className="hero-script-col">
              <div className="curved-script-wrap">
                <p className="curved-script-text">
                  A Brighter You<br />
                  A Kinder World
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            3. A JOURNEY OF SELF-DISCOVERY
            ========================================================================= */}
        <section className="discovery-section" id="about">
          <div className="discovery-container">
            {/* Left: Text & CTA */}
            <div className="discovery-text-col">
              <span className="section-eyebrow">A JOURNEY OF SELF-DISCOVERY</span>
              <h2 className="discovery-heading">
                Are your thoughts and emotions in your control?
              </h2>
              <h3 className="discovery-subheading">
                They are doing their own thing, isn&apos;t it?
              </h3>
              <p className="discovery-desc">
                InnerTruth provides structured learning, coaching and transformational programs designed to help individuals, families, students and professionals move from awareness to meaningful action.
              </p>
              <button
                type="button"
                className="btn-primary-pill mt-6"
                onClick={() => setModalOpen(true)}
              >
                Discover Your Inner Truth
                <IconArrowRight className="btn-arrow" />
              </button>
            </div>

            {/* Center: Meditating Woman in Nature */}
            <div className="discovery-image-col">
              <div className="peaceful-woman-frame">
                <Image
                  src="/images/new_design/meditating_woman_hd.png"
                  alt="Peaceful Indian woman meditating in lotus position outdoors in nature"
                  width={380}
                  height={380}
                  className="peaceful-woman-img"
                  priority
                />
              </div>
            </div>

            {/* Right: Quote */}
            <div className="discovery-quote-col">
              <div className="quote-box">
                <span className="quote-large-mark">“</span>
                <p className="quote-text">
                  When you change within,<br />
                  the world around you<br />
                  also changes.”
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. THE CORE PHILOSOPHY — THE 6 STEPS TO HUMAN EXCELLENCE
            ========================================================================= */}
        <section className="six-steps-section" id="six-steps">
          <div className="six-steps-container">
            <div className="six-steps-header">
              <span className="section-eyebrow">THE CORE PHILOSOPHY</span>
              <h2 className="section-heading">The 6 Steps to Human Excellence</h2>
            </div>

            <div className="stepper-diagram">
              {/* Step 1 */}
              <div className="stepper-node">
                <div className="stepper-badge">1</div>
                <div className="stepper-icon-circle">
                  <IconStepUnderstand size={42} />
                </div>
                <h4 className="stepper-title">UNDERSTAND</h4>
                <p className="stepper-subtitle">The Two Wings<br />of Human Excellence</p>
              </div>

              <div className="stepper-arrow" aria-hidden="true">→</div>

              {/* Step 2 */}
              <div className="stepper-node">
                <div className="stepper-badge">2</div>
                <div className="stepper-icon-circle">
                  <IconStepAware size={42} />
                </div>
                <h4 className="stepper-title">BECOME AWARE</h4>
                <p className="stepper-subtitle">Creation<br />of Personality</p>
              </div>

              <div className="stepper-arrow" aria-hidden="true">→</div>

              {/* Step 3 */}
              <div className="stepper-node">
                <div className="stepper-badge">3</div>
                <div className="stepper-icon-circle">
                  <IconStepTransform size={42} />
                </div>
                <h4 className="stepper-title">TRANSFORM</h4>
                <p className="stepper-subtitle">Habit &amp;<br />Autopilot</p>
              </div>

              <div className="stepper-arrow" aria-hidden="true">→</div>

              {/* Step 4 */}
              <div className="stepper-node">
                <div className="stepper-badge">4</div>
                <div className="stepper-icon-circle">
                  <IconStepComplete size={42} />
                </div>
                <h4 className="stepper-title">COMPLETE</h4>
                <p className="stepper-subtitle">Power of<br />Completion</p>
              </div>

              <div className="stepper-arrow" aria-hidden="true">→</div>

              {/* Step 5 */}
              <div className="stepper-node">
                <div className="stepper-badge">5</div>
                <div className="stepper-icon-circle">
                  <IconStepCreate size={42} />
                </div>
                <h4 className="stepper-title">CREATE</h4>
                <p className="stepper-subtitle">Creating<br />the Future</p>
              </div>

              <div className="stepper-arrow" aria-hidden="true">→</div>

              {/* Step 6 */}
              <div className="stepper-node">
                <div className="stepper-badge">6</div>
                <div className="stepper-icon-circle">
                  <IconStepLive size={42} />
                </div>
                <h4 className="stepper-title">LIVE</h4>
                <p className="stepper-subtitle">Unlocking<br />Human Excellence</p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. HOW INNERTRUTH SUPPORTS YOU (6 CARDS GRID)
            ========================================================================= */}
        <section className="supports-section" id="programs">
          <div className="supports-container">
            <div className="supports-header">
              <h2 className="supports-title">HOW INNERTRUTH SUPPORTS YOU</h2>
              <p className="supports-substrip">
                Sessions &nbsp;|&nbsp; Training &nbsp;|&nbsp; Guidance &nbsp;|&nbsp; Transformation
              </p>
            </div>

            <div className="supports-grid">
              {/* Card 1: Individuals */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_individuals.jpg"
                    alt="Individuals Counseling and Clarity"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Individuals</h3>
                <p className="support-card-desc">Personal clarity &amp; transformation</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>

              {/* Card 2: Schools */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_schools.jpg"
                    alt="Schools Life Skills Programs"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Schools</h3>
                <p className="support-card-desc">Life skills for a brighter future</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>

              {/* Card 3: Colleges */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_colleges.jpg"
                    alt="Colleges Student Confidence & Leadership"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Colleges</h3>
                <p className="support-card-desc">Confidence, responsibility &amp; success</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>

              {/* Card 4: Parents & Families */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_families.jpg"
                    alt="Parents & Families Counseling"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Parents &amp; Families</h3>
                <p className="support-card-desc">Understanding, communication &amp; harmony</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>

              {/* Card 5: Corporates */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_corporates.jpg"
                    alt="Corporates Awareness & Performance"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Corporates</h3>
                <p className="support-card-desc">Awareness for high-performing teams</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>

              {/* Card 6: Groups */}
              <article className="support-card">
                <div className="support-card-img-wrap">
                  <Image
                    src="/images/new_design/support_groups.jpg"
                    alt="Interactive Group Learning"
                    fill
                    className="support-card-img"
                    sizes="(max-width: 768px) 100vw, 20vw"
                  />
                </div>
                <h3 className="support-card-title">Groups</h3>
                <p className="support-card-desc">Interactive learning for real change</p>
                <button
                  type="button"
                  className="support-card-link"
                  onClick={() => setModalOpen(true)}
                >
                  Learn More →
                </button>
              </article>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. FOUNDER & THE BOOK SPLIT SECTION
            ========================================================================= */}
        <section className="founder-book-section" id="the-book">
          <div className="founder-book-container">
            {/* Left: Founder Card */}
            <div className="founder-card-col">
              <div className="founder-photo-wrap">
                <Image
                  src="/images/new_design/dr_shyam.jpg"
                  alt="Dr. (H.C.) Shyam Kumar - Founder InnerTruth"
                  width={210}
                  height={260}
                  className="founder-photo-img"
                  priority
                />
              </div>

              <div className="founder-details-wrap">
                <span className="section-eyebrow">MEET</span>
                <h3 className="founder-name">Dr. (H.C.) Shyam Kumar</h3>
                <p className="founder-title">Founder – InnerTruth</p>
                <p className="founder-bio">
                  With a passion for human development and transformation, Dr. (H.C.) Shyam Kumar conducts sessions and training programs to help individuals, families, students and professionals unlock their true potential.
                </p>

                <p className="founder-tags">
                  Trainer &nbsp;|&nbsp; Facilitator &nbsp;|&nbsp; Life Coach &nbsp;|&nbsp; Healer
                </p>

                <button
                  type="button"
                  className="btn-primary-pill"
                  onClick={() => setModalOpen(true)}
                >
                  Know More About InnerTruth
                  <IconArrowRight className="btn-arrow" />
                </button>
              </div>
            </div>

            {/* Right: The Book Card */}
            <div className="book-card-col">
              <div className="book-mockup-wrap">
                <Image
                  src="/images/new_design/book_mockup.jpg"
                  alt="Inner Truth: 6 Steps For Human Excellence Book by Dr. (H.C.) Shyam Kumar"
                  width={200}
                  height={280}
                  className="book-mockup-img"
                />
              </div>

              <div className="book-details-wrap">
                <span className="section-eyebrow">THE BOOK THAT BRINGS CLARITY</span>
                <h3 className="book-title">
                  Inner Truth: 6 Steps For Human Excellence
                </h3>
                <p className="book-subtitle">By Dr. (H.C.) Shyam Kumar</p>

                <div className="book-points">
                  <p>The book provides the clarity</p>
                  <p>Sessions and training support implementation.</p>
                  <p>Daily practice creates transformation.</p>
                </div>

                <button
                  type="button"
                  className="btn-primary-pill"
                  onClick={() => setModalOpen(true)}
                >
                  Explore the Book
                  <IconArrowRight className="btn-arrow" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            7. YOUR TRANSFORMATION JOURNEY (DEEP NAVY BANNER)
            ========================================================================= */}
        <section className="journey-banner-section">
          <div className="journey-banner-container">
            {/* Left Title */}
            <div className="journey-title-col">
              <span className="journey-eyebrow">YOUR TRANSFORMATION JOURNEY</span>
              <h2 className="journey-heading">
                Understand. Experience. Apply.<br />
                Transform. Live.
              </h2>
            </div>

            {/* Right 5-Step Stepper */}
            <div className="journey-steps-col">
              {/* Step 1 */}
              <div className="journey-step-item">
                <div className="journey-icon-circle">
                  <IconJourneyUnderstand />
                </div>
                <h4 className="journey-step-title">UNDERSTAND</h4>
                <p className="journey-step-sub">Read the book</p>
              </div>

              <div className="journey-arrow" aria-hidden="true">→</div>

              {/* Step 2 */}
              <div className="journey-step-item">
                <div className="journey-icon-circle">
                  <IconJourneyExperience />
                </div>
                <h4 className="journey-step-title">EXPERIENCE</h4>
                <p className="journey-step-sub">Attend sessions and training</p>
              </div>

              <div className="journey-arrow" aria-hidden="true">→</div>

              {/* Step 3 */}
              <div className="journey-step-item">
                <div className="journey-icon-circle">
                  <IconJourneyApply />
                </div>
                <h4 className="journey-step-title">APPLY</h4>
                <p className="journey-step-sub">Use the tools in daily life</p>
              </div>

              <div className="journey-arrow" aria-hidden="true">→</div>

              {/* Step 4 */}
              <div className="journey-step-item">
                <div className="journey-icon-circle">
                  <IconJourneyTransform />
                </div>
                <h4 className="journey-step-title">TRANSFORM</h4>
                <p className="journey-step-sub">Create lasting change</p>
              </div>

              <div className="journey-arrow" aria-hidden="true">→</div>

              {/* Step 5 */}
              <div className="journey-step-item">
                <div className="journey-icon-circle">
                  <IconJourneyLive />
                </div>
                <h4 className="journey-step-title">LIVE</h4>
                <p className="journey-step-sub">A happier, healthier, more purposeful life</p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            8. REAL STORIES — WHAT OUR PARTICIPANTS SAY (TESTIMONIALS)
            ========================================================================= */}
        <section className="testimonials-section" id="testimonials">
          <div className="testimonials-container">
            <div className="testimonials-header">
              <span className="section-eyebrow">REAL STORIES</span>
              <h2 className="section-heading">What Our Participants Say</h2>
            </div>

            <TestimonialSlider />
          </div>
        </section>

        {/* =========================================================================
            9. READY TO BEGIN YOUR JOURNEY (CTA BANNER WITH MOUNTAINS)
            ========================================================================= */}
        <section className="cta-mountains-section" id="contact">
          <div className="cta-mountains-bg-wrap">
            <Image
              src="/images/new_design/hero_bg.jpg"
              alt="Mountain Sunset Background"
              fill
              className="cta-mountains-img"
              sizes="100vw"
            />
            <div className="cta-mountains-overlay" />
          </div>

          <div className="cta-mountains-container">
            <div className="cta-mountains-center-col">
              <h2 className="cta-mountains-heading">Ready to begin your journey?</h2>
              <p className="cta-mountains-subheading">Understand. Transform. Create. Live.</p>
              <button
                type="button"
                className="btn-primary-pill"
                onClick={() => setModalOpen(true)}
              >
                Talk to InnerTruth
                <IconArrowRight className="btn-arrow" />
              </button>
            </div>

            <div className="cta-mountains-contact-col">
              <a href={`tel:${PHONE}`} className="cta-contact-item">
                <span className="contact-icon-circle green-phone">
                  <IconPhoneContact size={15} />
                </span>
                <span className="contact-item-text">{PHONE_DISPLAY}</span>
              </a>

              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="cta-contact-item">
                <span className="contact-icon-circle green-wa">
                  <IconWhatsAppContact size={15} />
                </span>
                <span className="contact-item-text">WhatsApp</span>
              </a>

              <a href={`mailto:${EMAIL}`} className="cta-contact-item">
                <span className="contact-icon-circle blue-email">
                  <IconEmailContact size={15} />
                </span>
                <span className="contact-item-text">{EMAIL}</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* 10. Footer */}
      <FooterNew />
    </div>
  );
}
